import React, { useEffect, useState, useRef } from "react";
import BodyContainer from "../components/BodyContainer";
import BottomContainer from "../components/BottomContainer";
import Footer from "../components/Footer";
import FooterLinks from "../components/FooterLinks";
import { Helmet } from "react-helmet-async";
import bannerImage from "../assets/banner4.png";
import Banner from "../components/Banner";
import { Link } from "react-router-dom";
import "../Products.css";
import "../Site.css";
import ScrollingBanner from "../components/scrollingBanner";

function Products() {
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [states, setStates] = useState([]);
  const [cartMessage, setCartMessage] = useState("");
  const [previewFile, setPreviewFile] = useState(null);
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const [descriptionPopupVisible, setDescriptionPopupVisible] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState(new Set());
  const loginMessageRef = useRef(null);

  const categoryOrder = [
    "Naplex Practice Exams, Study Guides, and Practice Questions",
    "UMPJE/MPJE Practice Exams and Study Guides",
    "Bundle and Save",
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setCartItems(user?.Cart || []);

    async function fetchSubscriptions() {
      if (!user) return;

      try {
        const response = await fetch(`${API_URL}/api/subscriptions/${user.Id}/active`);
        if (!response.ok) throw new Error("Failed to fetch subscriptions");

        const data = await response.json();

        if (Array.isArray(data.subscriptions)) {
          const normalizedSubscriptions = new Set(
            data.subscriptions
              .flatMap((sub) => sub.Products || [])
              .filter(Boolean)
              .map((id) => String(id))
          );

          setActiveSubscriptions(normalizedSubscriptions);
        } else {
          console.error("Unexpected format for subscriptions data:", data);
        }
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
      }
    }

    fetchSubscriptions();

    const handleCartUpdate = () => {
      const updatedUser = JSON.parse(localStorage.getItem("user"));
      setCartItems(updatedUser?.Cart || []);
    };

    window.addEventListener("cartUpdated", handleCartUpdate);

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);

  useEffect(() => {
    async function fetchStates() {
      try {
        const response = await fetch(`${API_URL}/api/products/states`);
        const data = await response.json();
        setStates(Array.isArray(data) ? data : data.states || []);
      } catch (error) {
        console.error("Error fetching states:", error);
        setStates([]);
      }
    }

    fetchStates();
  }, []);

  async function fetchProducts() {
    try {
      const queryParams = new URLSearchParams();

      if (searchTerm) queryParams.append("search", searchTerm);
      if (selectedState) queryParams.append("state", selectedState);

      const response = await fetch(`${API_URL}/api/products?${queryParams}`);
      const data = await response.json();
      const productsData = Array.isArray(data) ? data : data.products || [];

      setProducts(productsData);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }

  const groupedProducts = products.reduce((acc, product) => {
    const category = product.Category || "Other Products";

    if (!acc[category]) {
      acc[category] = [];
    }

    acc[category].push(product);
    return acc;
  }, {});

  const featuredIds = new Set(featuredProducts.map((p) => String(p.Id)));

  const filteredGroupedProducts = Object.keys(groupedProducts).reduce((acc, category) => {
    const filtered = groupedProducts[category].filter(
      (product) => !featuredIds.has(String(product.Id))
    );

    if (filtered.length > 0) {
      acc[category] = filtered;
    }

    return acc;
  }, {});

  const sortedCategories = [
    ...categoryOrder.filter((cat) => filteredGroupedProducts[cat]),
    ...Object.keys(filteredGroupedProducts).filter((cat) => !categoryOrder.includes(cat)),
  ];

  async function fetchFeaturedProducts() {
    try {
      const response = await fetch(`${API_URL}/api/products/featured`);
      const data = await response.json();
      const productsData = Array.isArray(data) ? data : data.products || [];

      setFeaturedProducts(productsData);
    } catch (error) {
      console.error("Error fetching featured products:", error);
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  }

  async function handlePreview(productId) {
    try {
      const response = await fetch(`${API_URL}/api/courses/by-product/${productId}`);

      if (response.ok) {
        const courseData = await response.json();

        if (courseData.course?.FreeFileURL) {
          setPreviewFile(courseData.course.FreeFileURL);
        } else {
          alert("No preview available for this course.");
        }
      } else {
        console.error(`Failed to fetch FreeFileURL for product ${productId}`);
        alert("Failed to load preview.");
      }
    } catch (error) {
      console.error(`Error fetching FreeFileURL for product ${productId}:`, error);
      alert("An error occurred while fetching the preview.");
    }
  }

  useEffect(() => {
    fetchFeaturedProducts();
    fetchProducts();
  }, [searchTerm, selectedState]);

  const handleAddToCart = async (productId) => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      setCartMessage("Please Click Here to Log in to add items to your Cart.");

      setTimeout(() => {
        loginMessageRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);

      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/cart/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.Id, productId }),
      });

      if (!response.ok) throw new Error("Failed to add item to cart.");

      const updatedCart = [...(user.Cart || []), productId];
      user.Cart = updatedCart;
      localStorage.setItem("user", JSON.stringify(user));

      setCartMessage("Item Added to Cart! CLICK HERE to Navigate to Your Cart");

      setTimeout(() => {
        loginMessageRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);

      setCartItems(updatedCart);
      window.dispatchEvent(new Event("cartUpdated"));
    } catch (error) {
      console.error("Error adding to cart:", error);
      setCartMessage("Error adding item to cart.");
    }

    setTimeout(() => setCartMessage(""), 60000);
  };

  useEffect(() => {
    if (cartMessage) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [cartMessage]);

const CartIconButton = () => (
  <div className="products-cart-top-row">
    <Link
      to="/cart"
      className="products-navbar-style-cart"
      aria-label="Navigate to Your Cart"
    >
      <img
        src="https://cdn-icons-png.flaticon.com/512/833/833314.png"
        alt="Cart Icon"
        className="products-navbar-cart-icon"
      />

      {cartItems.length > 0 && (
        <span className="products-navbar-cart-badge">{cartItems.length}</span>
      )}

      <span className="products-cart-tooltip">Navigate to Your Cart</span>
    </Link>
  </div>
);

  return (
    <>
      <Helmet>
        <title>PharmPrepPro - Products</title>
        
      </Helmet>
      <BottomContainer>
        <Banner height="300px" image={bannerImage} />

        <BodyContainer>
          {cartMessage && (
            <div className="cart-message-wrapper" ref={loginMessageRef}>
              {cartMessage === "Please Click Here to Log in to add items to your Cart." ? (
                <Link to="/login" className="cart-message-card cart-warning">
                  <span className="cart-message-icon">🔐</span>
                  <span>Please log in to add items to your cart.</span>
                </Link>
              ) : cartMessage === "Item Added to Cart! CLICK HERE to Navigate to Your Cart" ? (
                <Link to="/cart" className="cart-message-card cart-success">
                  <span className="cart-message-icon">✅</span>
                  <span>Item added to your cart. CLICK HERE to go to your cart.</span>
                </Link>
              ) : (
                <div className="cart-message-card cart-error">
                  <span className="cart-message-icon">⚠️</span>
                  <span>{cartMessage}</span>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <h2>Loading Products...</h2>
          ) : (
            <div className="products-page-content">
              <CartIconButton />

              {/* Featured Products */}
              <h1 className="white-text product-heading">Featured Products</h1>
              <div className="products-container">
                {featuredProducts.map((product) => (
                  <div key={product.Id} className="product-card">
                    <div className="product-badge">★ Featured</div>

                    <img
                      src={product.PreSignedURL || "/placeholder.png"}
                      alt={product.Name}
                      className="product-image"
                    />

                    <button
                      className="description-toggle"
                      onClick={() => setDescriptionPopupVisible(product.Id)}
                    >
                      {descriptionPopupVisible === product.Id
                        ? "Hide Description ▲"
                        : "Show Description ▼"}
                    </button>

                    <p className="product-price">
                      {product.PriceOptions &&
                      Object.entries(product.PriceOptions).length > 0 ? (
                        (() => {
                          const [_, firstPrice] = Object.entries(product.PriceOptions)[0];
                          return <>Starting at ${firstPrice}</>;
                        })()
                      ) : (
                        "N/A"
                      )}
                    </p>

                    <div className="button-row">
                      {product.ProductType === "Course" && (
                        <button
                          className="preview-button"
                          onClick={() => handlePreview(product.Id)}
                        >
                          Preview
                        </button>
                      )}

                      <button
                        className="buy-button"
                        onClick={() => handleAddToCart(product.Id)}
                        disabled={
                          cartItems.includes(String(product.Id)) ||
                          activeSubscriptions.has(String(product.Id)) ||
                          (product.ProductType === "Bundle" &&
                            (product.ProductsIncluded || []).some((id) =>
                              activeSubscriptions.has(String(id))
                            ))
                        }
                      >
                        {cartItems.includes(String(product.Id))
                          ? "In Cart"
                          : activeSubscriptions.has(String(product.Id))
                          ? "Subscribed"
                          : product.ProductType === "Bundle" &&
                            (product.ProductsIncluded || []).some((id) =>
                              activeSubscriptions.has(String(id))
                            )
                          ? "Some Product(s) Owned"
                          : "Add to Cart"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Categories */}
              {sortedCategories.map((category) => (
                <div key={category}>
                  <h1 className="white-text product-heading">{category}</h1>

                  <div className="products-container">
                    {[...filteredGroupedProducts[category]]
                      .sort((a, b) => (a.Display || 999) - (b.Display || 999))
                      .map((product) => (
                        <div key={product.Id} className="product-card">
                          <img
                            src={product.PreSignedURL || "/placeholder.png"}
                            alt={product.Name}
                            className="product-image"
                          />

                          <button
                            className="description-toggle"
                            onClick={() => setDescriptionPopupVisible(product.Id)}
                          >
                            {descriptionPopupVisible === product.Id
                              ? "Hide Description ▲"
                              : "Show Description ▼"}
                          </button>

                          <p className="product-price">
                            {product.PriceOptions &&
                            Object.entries(product.PriceOptions).length > 0 ? (
                              (() => {
                                const [_, firstPrice] =
                                  Object.entries(product.PriceOptions)[0];
                                return <>Starting at ${firstPrice}</>;
                              })()
                            ) : (
                              "N/A"
                            )}
                          </p>

                          <div className="button-row">
                            {product.ProductType === "Course" && (
                              <button
                                className="preview-button"
                                onClick={() => handlePreview(product.Id)}
                              >
                                Preview
                              </button>
                            )}

                            <button
                              className="buy-button"
                              onClick={() => handleAddToCart(product.Id)}
                              disabled={
                                cartItems.includes(String(product.Id)) ||
                                activeSubscriptions.has(String(product.Id)) ||
                                (product.ProductType === "Bundle" &&
                                  (product.ProductsIncluded || []).some((id) =>
                                    activeSubscriptions.has(String(id))
                                  ))
                              }
                            >
                              {cartItems.includes(String(product.Id))
                                ? "In Cart"
                                : activeSubscriptions.has(String(product.Id))
                                ? "Subscribed"
                                : product.ProductType === "Bundle" &&
                                  (product.ProductsIncluded || []).some((id) =>
                                    activeSubscriptions.has(String(id))
                                  )
                                ? "Some Product(s) Owned"
                                : "Add to Cart"}
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </BodyContainer>
      </BottomContainer>

      <FooterLinks />
      <Footer />

      {/* Product Description Modal */}
      {descriptionPopupVisible &&
        (() => {
          const selectedProduct = [...products, ...featuredProducts].find(
            (p) => p.Id === descriptionPopupVisible
          );

          if (!selectedProduct) return null;

          return (
            <div
              className="description-modal"
              onClick={() => setDescriptionPopupVisible(null)}
            >
              <div
                className="description-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="description-image-container">
                  <img
                    src={selectedProduct.PreSignedURL || "/placeholder.png"}
                    alt={selectedProduct.Name}
                    className="description-product-image"
                  />
                </div>

                <div className="description-header">
                  <h2 className="description-title">{selectedProduct.Name}</h2>

                  <p className="description-price">
                    {selectedProduct.PriceOptions &&
                    Object.entries(selectedProduct.PriceOptions).length > 0 ? (
                      (() => {
                        const [_, firstPrice] =
                          Object.entries(selectedProduct.PriceOptions)[0];
                        return <>Starting at ${firstPrice}</>;
                      })()
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>

                <div className="description-body">
                  <p className="product-description">
                    {selectedProduct.Description}
                  </p>
                </div>

                <div className="description-footer">
                  {selectedProduct.ProductType === "Course" && (
                    <button
                      className="preview-button"
                      onClick={() => {
                        setDescriptionPopupVisible(null);
                        handlePreview(selectedProduct.Id);
                      }}
                    >
                      Preview
                    </button>
                  )}

                  <button
                    className="buy-button"
                    onClick={() => {
                      setDescriptionPopupVisible(null);
                      handleAddToCart(selectedProduct.Id);
                    }}
                    disabled={
                      cartItems.includes(String(selectedProduct.Id)) ||
                      activeSubscriptions.has(String(selectedProduct.Id)) ||
                      (selectedProduct.ProductType === "Bundle" &&
                        (selectedProduct.ProductsIncluded || []).some((id) =>
                          activeSubscriptions.has(String(id))
                        ))
                    }
                  >
                    {cartItems.includes(String(selectedProduct.Id))
                      ? "In Cart"
                      : activeSubscriptions.has(String(selectedProduct.Id))
                      ? "Subscribed"
                      : "Add to Cart"}
                  </button>

                  <button
                    className="close-button"
                    onClick={() => setDescriptionPopupVisible(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Preview Modal */}
      {previewFile && (
        <div className="preview-modal" onClick={() => setPreviewFile(null)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <h2 className="preview-title">Course Preview</h2>
              <p className="preview-subtitle">
                Review the sample content before purchasing.
              </p>
            </div>

            <iframe
              src={`${previewFile}#toolbar=1&navpanes=0&scrollbar=1&zoom=100`}
              title="Course Preview"
              className="preview-iframe"
            />

            <div className="preview-footer">
              <button
                type="button"
                className="close-button"
                onClick={() => setPreviewFile(null)}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Products;